import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import uniqid from "uniqid";
import moment from "moment";

import { getCommSelectedComponents, getUpgradesToAdd } from "utils/communities";
import { getCompAreaForCostCalc } from "utils/enclosure/components";
import { calcUpgradeDetailedCost } from "utils/upgrades";
import { generationCostTypes, unitCostTypes } from "utils/costTypeIndex";
import { useStateSelector } from "utils/field";

import Dialog, { CloseDialog } from "components/Dialog";
import Select from "components/Input/Select";
import Button from "components/Button";
import Loading from "components/Loading";
import Input from "components/Input";

import { selectUsersOrganization, selectUserUid } from "store/users/selectors";
import { selectSaveUpgradePackageLabels } from "store/upgradeLibrary/selectors";
import { selectCommunityData } from "store/community/selectors";
import { addUpgradeToCommunity } from "store/community/actions";
import { fetchUpgradeLabels, fetchUpgradePackage } from "store/upgradeLibrary/thunk";
import { actions as commUpgradeActions } from "features/Community/Upgrades/_ducks";

import classes from "./style.module.scss";

const { addCommUpgradePackage, adjustCommPackageCost } = commUpgradeActions;

const AddPackageDialog = ({
    isOpen,
    toggleOpen,
    upgradePackageIds,
    selected,
    selectedName,
    toggleSelected,
    numberOfPackages = 0,
    tableRef = null,
    userUid,
    userOrganization,
    upgradePackageLabels,
    communityData,
    fetchUpgradeLabels,
    fetchUpgradePackage,
    adjustCommPackageCost,
    addCommUpgradePackage,
    addUpgradeToCommunity,
}) => {
    const [packageName, updatePackageName] = useState("");

    const [isLoadingLabels, setIsLoadingLabels] = useState(false);
    const [isLoadingPackage, setIsLoadingPackage] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState({});

    const primaryOrgId = Object.keys(userOrganization?.memberOrgIds || {}).includes("primaryOrg")
        ? userOrganization?.memberOrgIds?.primaryOrg || ""
        : Object.keys(userOrganization?.memberOrgIds || {})?.[0] || "";

    const orgId = !isEmpty(userOrganization) ? primaryOrgId : "";

    const { ecmInputMap = {}, modelData = {} } = communityData || {};

    useEffect(() => {
        setIsLoadingLabels(true);

        const fetchUpgradeLabelsAsync = async () => {
            await fetchUpgradeLabels(userUid, orgId);

            setIsLoadingLabels(false);
        };

        if (userUid && isOpen) {
            fetchUpgradeLabelsAsync(userUid, orgId);
        }
    }, [userUid, orgId, fetchUpgradeLabels, isOpen]);

    const organizedUpgradePackageLabels = upgradePackageLabels
        ? [
              ...upgradePackageLabels.upgradeLibMap,
              ...upgradePackageLabels.orgUpgradeLibMap.map(({ id, name }) => ({
                  name,
                  id,
                  isFromOrg: true,
              })),
          ]
        : [];

    const handleSave = useCallback(async () => {
        let packageId = selected;
        let dateObj = {};
        let uiObj = {};

        // Create new package
        if (!selected) {
            packageId = uniqid("package-");
            dateObj = {
                created: moment().format("LLLL"),
                lastEdited: moment().format("LLLL"),
            };
            uiObj = { ui: { displayOrder: numberOfPackages } };
        }

        // Add new package to the community's upgrades state (Populates upgrades table)
        addCommUpgradePackage({
            packageId,
            packageUpdates: {
                name: packageName,
                ...dateObj,
                ...uiObj,
            },
        });

        if (tableRef && tableRef.current) tableRef.current.scrollLeft = tableRef.current.scrollWidth;

        toggleOpen(false);
        toggleSelected("");
        updatePackageName("");
        setSelectedPackage({});
    }, [selected, packageName, toggleOpen, toggleSelected, numberOfPackages, tableRef]);

    const componentsFromCommunity =
        Object.fromEntries(
            Object.entries(modelData?.models || {}).map(([modelId, { components }]) => [modelId, components])
        ) || {};
    const communityModelIds = Object.keys(modelData?.models) || [];

    const handleLoad = async () => {
        setIsLoadingPackage(true);

        const packageToAdd = await fetchUpgradePackage(
            userUid,
            selectedPackage.id,
            true,
            selectedPackage.isFromOrg ? orgId : null
        );

        const isPackageExists = upgradePackageIds.includes(selectedPackage.id);

        let packageId = selectedPackage.id;

        // Give new package and upgrades unique IDs (If package already exists)
        if (isPackageExists) {
            packageId = uniqid("-");

            if (packageToAdd.upgrades) {
                const newUpgradeIds = Object.keys(packageToAdd.upgrades).map((key) => [
                    uniqid("upgrade-"),
                    packageToAdd.upgrades[key],
                ]);

                packageToAdd.upgrades = Object.fromEntries(newUpgradeIds);
            }
        }

        const updatedPackage = { ...packageToAdd, id: packageId };

        const packageCost = {};

        // Calculate costs for each upgrade
        if (updatedPackage.upgrades) {
            Object.keys(updatedPackage.upgrades).map((upgradeId) => {
                const {
                    cost,
                    fields,
                    upgradeType: upgradeH2kType,
                    upgradeSubType: upgradeType,
                } = updatedPackage.upgrades[upgradeId] || {};

                // Select all components of this type for each base model in community
                const selectedComponents = getCommSelectedComponents(
                    upgradeType,
                    upgradeH2kType,
                    componentsFromCommunity
                );

                if (!isEmpty(selectedComponents) && cost != null) {
                    // Helper function to return component area/count or pv capacity
                    const getTotalUnitValue =
                        (modelId) =>
                        (calcType = "matCostArea") => {
                            if (upgradeType === "generation") return fields?.pvCapacity || 0;

                            return parseFloat(
                                getCompAreaForCostCalc(
                                    selectedComponents[modelId],
                                    componentsFromCommunity[modelId],
                                    upgradeType,
                                    calcType
                                ).toFixed(2)
                            );
                        };

                    Object.entries(cost).forEach(([tableUpgradeType, { total = 0, items: costItems = {} }]) => {
                        // Initialize community totals and cost items for upgrade
                        const communityTotals = {};

                        const communityCostItems = Object.entries(costItems).reduce((acc, [itemId, cur]) => {
                            const newCostItem = { ...cur, total: {} };

                            if (unitCostTypes.includes(cur.label)) {
                                newCostItem.totalArea = {};
                            } else if (generationCostTypes.includes(cur.label)) {
                                newCostItem.totalPvCapacity = {};
                            }

                            return { ...acc, [itemId]: newCostItem };
                        }, {});

                        // Calculate total upgrade cost and cost items for each model in community
                        communityModelIds.forEach((modelId) => {
                            const { totalCost = 0, costItems: modelCostItems = {} } = calcUpgradeDetailedCost(
                                costItems,
                                getTotalUnitValue(modelId)
                            );

                            Object.entries(modelCostItems).forEach(
                                ([itemId, { total = 0, label = "", totalArea = 0, totalPvCapacity = 0 }]) => {
                                    if (unitCostTypes.includes(label)) {
                                        communityCostItems[itemId].totalArea[modelId] = totalArea;
                                    } else if (generationCostTypes.includes(label)) {
                                        communityCostItems[itemId].totalPvCapacity[modelId] = totalPvCapacity;
                                    }
                                    communityCostItems[itemId].total[modelId] = total;
                                }
                            );
                            communityTotals[modelId] = totalCost;

                            if (modelId in packageCost) packageCost[modelId] += totalCost;
                            else packageCost[modelId] = totalCost;
                        });

                        // Update upgrade's packageID reference, community totals, and community cost items after applying package
                        updatedPackage.upgrades[upgradeId].upgradePackageId = packageId;
                        updatedPackage.upgrades[upgradeId].cost[tableUpgradeType] = {
                            ...updatedPackage.upgrades[upgradeId].cost[tableUpgradeType],
                            communityTotals: communityTotals,
                            communityItems: communityCostItems,
                        };
                    });
                }
            });

            // Update community's ecmInputMap with saved upgrades in duplicated package (For running simulations)
            const upgradesToAdd = getUpgradesToAdd(updatedPackage.upgrades, ecmInputMap);

            upgradesToAdd.forEach((upgrade) => {
                const upgradeType = Object.values(upgrade)[0].packageValues.upgradeSubType;
                const upgradeKey = Object.keys(upgrade)[0];

                const upgradeWithPackageId = {
                    [upgradeKey]: {
                        ...upgrade[upgradeKey],
                        packageValues: {
                            ...upgrade[upgradeKey].packageValues,
                            upgradePackageId: packageId,
                        },
                    },
                };

                addUpgradeToCommunity(upgradeType, upgradeWithPackageId);
            });
        }

        // Set package's capital cost for each community model
        communityModelIds.forEach((modelId) => {
            adjustCommPackageCost({
                modelId,
                packageId,
                amount: packageCost[modelId],
            });
        });

        // Add loaded Upgrade Package to commUpgrades state (Populates upgrades table)
        addCommUpgradePackage({
            packageId,
            packageUpdates: {
                ...updatedPackage,
                name: isPackageExists ? `${packageToAdd.name} (copy)` : packageToAdd.name,
                ui: { displayOrder: numberOfPackages },
            },
        });

        // Object.entries(updatedPackage.upgrades || {})
        //     .forEach(async ([upgradeId, upgrade]) => {
        //         const upgradeSubType = upgrade.upgradeSubType || Object.keys(upgrade.label)[0];
        //         const upgradeToAdd = getCommUpgradeToAdd(
        //             upgrade,
        //             {
        //                 upgradeKey: upgradeId,
        //                 upgradeSubType: upgradeSubType,
        //                 upgradeType: upgrade.upgradeType,
        //                 parentUpgradeKey: null,
        //                 upgradePackageId: packageId,
        //             },
        //             commEcmInputMap,
        //             0,
        //             true
        //         );
        //         addUpgradeToCommunity(upgradeSubType, upgradeToAdd);
        //     });

        setIsLoadingPackage(false);
        toggleOpen(false);
    };

    const handleClose = useCallback(() => {
        toggleOpen(false);
        toggleSelected("");
        updatePackageName("");
        setSelectedPackage({});
    }, [toggleOpen, toggleSelected]);

    useEffect(() => {
        updatePackageName(selectedName);
    }, [selectedName]);

    return (
        <Dialog
            open={isOpen}
            classes={{
                rootClass: "",
                paperClass: classes.dialogPaper,
            }}
            // onBackdropClick={() => setIsAddingPackage(false)}
        >
            <CloseDialog closeActions={handleClose} />
            {false && isLoadingLabels ? (
                <Loading />
            ) : (
                <div className={classes.packageDialogContent}>
                    <h2>Add Upgrade Package</h2>
                    <div className={classes.inputButtonWrapper}>
                        <Input
                            type="text"
                            name={"packageName"}
                            label={`Create New Package`}
                            placeholder="New upgrade package name"
                            //validate={validate}
                            hideLabel
                            input={{
                                value: packageName || "",
                                onChange: (val) => updatePackageName(val),
                            }}
                        />
                        <span data-tip="Name field is required" data-for="dialogTip">
                            <Button
                                onClick={handleSave}
                                disabled={!packageName}
                                style={{ height: "36px", width: "6.0625rem" }}
                            >
                                {selected ? "Update" : "Create"}
                            </Button>
                        </span>
                    </div>
                    <div className={classes.divider}></div>
                    <div className={classes.inputButtonWrapper}>
                        <Select
                            type="text"
                            name={"packageName"}
                            label={`Load Saved Package`}
                            placeholder="Select saved upgrade package"
                            //validate={validate}
                            disabled={!organizedUpgradePackageLabels}
                            hideLabel
                            search
                            options={
                                organizedUpgradePackageLabels
                                    ? organizedUpgradePackageLabels.map(({ id, name, isFromOrg }) => ({
                                          label: name,
                                          value: { name, id, isFromOrg: !!isFromOrg },
                                      }))
                                    : []
                            }
                            input={{
                                value: selectedPackage,
                                onChange: (val) => setSelectedPackage(val),
                            }}
                        />
                        <span data-tip="Selected package is required" data-for="dialogTip">
                            <Button
                                onClick={handleLoad}
                                disabled={isEmpty(selectedPackage) || isLoadingPackage}
                                style={{ height: "36px", width: "6.0625rem" }}
                            >
                                {isLoadingPackage ? "Applying..." : "Apply"}
                            </Button>
                        </span>
                    </div>
                </div>
            )}
        </Dialog>
    );
};

const mapStateToProps = createStructuredSelector({
    userUid: selectUserUid,
    userOrganization: selectUsersOrganization,
    upgradePackageLabels: selectSaveUpgradePackageLabels,
    communityData: selectCommunityData,
    // commEcmInputMap: selectCommunityEcmInputMap,
});

const mapDispatchToProps = (dispatch) => ({
    fetchUpgradeLabels: (uid, orgId) => dispatch(fetchUpgradeLabels(uid, orgId)),
    fetchUpgradePackage: (uid, packageId, isFromOrg, orgId) =>
        dispatch(fetchUpgradePackage(uid, packageId, isFromOrg, orgId)),
    adjustCommPackageCost: ({ modelId, packageId, amount }) => {
        dispatch(adjustCommPackageCost({ modelId, packageId, amount }));
    },
    addCommUpgradePackage: ({ packageId, packageUpdates }) => {
        dispatch(addCommUpgradePackage({ packageId, packageUpdates }));
    },
    addUpgradeToCommunity: (sectionType, upgrade) => dispatch(addUpgradeToCommunity(sectionType, upgrade)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPackageDialog);
